import rawConfig from "@config";

/**
 * Build runtime configuration by merging defaults with environment overrides and applying
 * environment variable substitutions (including placeholder tokens like "__TOKEN__").
 */
const envSource =
  typeof __APP_CONFIG_ENV__ !== "undefined" && __APP_CONFIG_ENV__ != null
    ? __APP_CONFIG_ENV__
    : import.meta.env;

const env = { ...envSource };

const isPlainObject = (value) =>
  value !== null && typeof value === "object" && !Array.isArray(value);

const mergeDeep = (base = {}, override = {}) => {
  const result = isPlainObject(base) ? { ...base } : {};
  const keys = new Set([
    ...Object.keys(isPlainObject(base) ? base : {}),
    ...Object.keys(isPlainObject(override) ? override : {}),
  ]);

  keys.forEach((key) => {
    const baseValue = isPlainObject(base) ? base[key] : undefined;
    const overrideValue = isPlainObject(override) ? override[key] : undefined;

    if (overrideValue === undefined) {
      if (Array.isArray(baseValue)) {
        result[key] = baseValue.slice();
      } else if (isPlainObject(baseValue)) {
        result[key] = mergeDeep(baseValue, {});
      } else if (baseValue !== undefined) {
        result[key] = baseValue;
      }
      return;
    }

    if (Array.isArray(overrideValue)) {
      result[key] = overrideValue.slice();
      return;
    }

    if (isPlainObject(overrideValue)) {
      const baseChild = isPlainObject(baseValue) ? baseValue : {};
      result[key] = mergeDeep(baseChild, overrideValue);
      return;
    }

    result[key] = overrideValue;
  });

  return result;
};

const getFirstEnvValue = (...keys) => {
  for (const key of keys) {
    if (key && env[key] !== undefined && env[key] !== "") {
      return env[key];
    }
  }
  return undefined;
};

const normalizeEnvName = (name) => (name || "").toLowerCase();

const resolveEnvironmentKey = (environments) => {
  const envHints = [
    normalizeEnvName(env.VITE_APP_ENV),
    normalizeEnvName(env.VITE_ENV),
    normalizeEnvName(env.APP_ENV),
    normalizeEnvName(env.ENVIRONMENT),
  ].filter(Boolean);

  const mode = normalizeEnvName(env.MODE);
  const modeMap = { development: "dev", production: "prod", test: "dev" };
  if (mode) {
    envHints.push(mode);
    if (modeMap[mode]) {
      envHints.push(modeMap[mode]);
    }
  }

  for (const hint of envHints) {
    if (hint && environments[hint]) {
      return hint;
    }
  }

  if (environments.prod) {
    return "prod";
  }

  const available = Object.keys(environments);
  return available.length > 0 ? available[0] : undefined;
};

const applyPlaceholders = (value) => {
  if (typeof value === "string") {
    const placeholderMatch = value.match(/^__([A-Z0-9_]+)__$/);
    if (placeholderMatch) {
      const token = placeholderMatch[1];
      const candidateKeys = [
        token,
        `VITE_${token}`,
        `VITE_APP_${token}`,
        `VITE_${token.toUpperCase()}`,
        `VITE_APP_${token.toUpperCase()}`,
      ];
      const substituted = getFirstEnvValue(...candidateKeys);
      if (substituted !== undefined) {
        return substituted;
      }
    }
    return value;
  }

  if (Array.isArray(value)) {
    return value.map((item) => applyPlaceholders(item));
  }

  if (isPlainObject(value)) {
    const resolved = {};
    Object.entries(value).forEach(([key, val]) => {
      resolved[key] = applyPlaceholders(val);
    });
    return resolved;
  }

  return value;
};

const setNestedValue = (target, path, newValue) => {
  if (!target || path.length === 0) {
    return;
  }
  let cursor = target;
  for (let i = 0; i < path.length - 1; i += 1) {
    const segment = path[i];
    if (!isPlainObject(cursor[segment])) {
      cursor[segment] = {};
    }
    cursor = cursor[segment];
  }
  cursor[path[path.length - 1]] = newValue;
};

const coerceBoolean = (value) => {
  if (typeof value !== "string") {
    return Boolean(value);
  }
  const normalized = value.toLowerCase();
  if (normalized === "true" || normalized === "1") {
    return true;
  }
  if (normalized === "false" || normalized === "0") {
    return false;
  }
  return Boolean(value);
};

const coerceNumber = (value) => {
  if (typeof value === "number") {
    return value;
  }
  if (typeof value === "string" && value.trim() !== "" && !Number.isNaN(Number(value))) {
    return Number(value);
  }
  return value;
};

const coerceCsvArray = (value) => {
  if (Array.isArray(value)) {
    return value;
  }
  if (typeof value === "string") {
    return value
      .split(",")
      .map((item) => item.trim())
      .filter((item) => item.length > 0);
  }
  return value;
};

const applyExplicitOverrides = (configToMutate) => {
  const overrides = [
    {
      path: ["agentId"],
      keys: ["VITE_APP_AGENT_ID", "VITE_AGENT_ID"],
      transform: coerceNumber,
    },
    {
      path: ["auth", "clientId"],
      keys: ["VITE_APP_CLIENT_ID", "VITE_CLIENT_ID", "VITE_INCONTACT_CLIENT_ID"],
    },
    {
      path: ["auth", "clientSecret"],
      keys: ["VITE_APP_CLIENT_SECRET", "VITE_CLIENT_SECRET", "VITE_INCONTACT_CLIENT_SECRET"],
    },
    { path: ["auth", "issuer"], keys: ["VITE_APP_ISSUER", "VITE_AUTH_ISSUER"] },
    { path: ["title"], keys: ["VITE_APP_TITLE", "VITE_TITLE"] },
    {
      path: ["showHeader"],
      keys: ["VITE_APP_SHOW_HEADER", "VITE_SHOW_HEADER"],
      transform: coerceBoolean,
    },
    {
      path: ["auth", "returnUrl"],
      keys: [
        "VITE_APP_AUTH_RETURN_URL",
        "VITE_AUTH_RETURN_URL",
        "VITE_APP_RETURN_URL",
        "VITE_RETURN_URL",
      ],
    },
    {
      path: ["dataDog", "clientToken"],
      keys: [
        "VITE_DATADOG_CLIENT_TOKEN",
        "VITE_APP_DATADOG_CLIENT_TOKEN",
        "VITE_DATADOG_CLIENT_KEY",
      ],
    },
    {
      path: ["dataDog", "disabled"],
      keys: ["VITE_DATADOG_DISABLED", "VITE_APP_DATADOG_DISABLED"],
      transform: coerceBoolean,
    },
    {
      path: ["dataDog", "logLevels"],
      keys: ["VITE_DATADOG_LOG_LEVELS", "VITE_APP_DATADOG_LOG_LEVELS"],
      transform: coerceCsvArray,
    },
    {
      path: ["dataDog", "site"],
      keys: ["VITE_DATADOG_SITE", "VITE_APP_DATADOG_SITE"],
    },
  ];

  overrides.forEach(({ path, keys, transform }) => {
    const value = getFirstEnvValue(...keys);
    if (value !== undefined) {
      const normalized =
        typeof value === "string" && value.match(/^__([A-Z0-9_]+)__$/)
          ? value
          : transform
            ? transform(value)
            : value;
      setNestedValue(configToMutate, path, normalized);
    }
  });
};

const environments = rawConfig.environments || {};
const environmentKey = resolveEnvironmentKey(environments);
const mergedConfig = mergeDeep(
  rawConfig.defaults || {},
  environmentKey ? environments[environmentKey] : {},
);
const resolvedConfig = applyPlaceholders(mergedConfig);
applyExplicitOverrides(resolvedConfig);

// Allow URL query parameters to override agentId and showHeader during local development
if (env.DEV && typeof window !== "undefined") {
  const params = new URLSearchParams(window.location.search);
  if (params.has("agentId")) {
    resolvedConfig.agentId = params.get("agentId");
  }
  if (params.has("showHeader")) {
    const sh = params.get("showHeader");
    resolvedConfig.showHeader = sh === "1" || sh.toLowerCase() === "true";
  }
}

export default resolvedConfig;
